import { isBrowser } from "./platform";

/**
 * addEventListeners
 *
 * Allow multiple events to be added in one call, jQuery-style
 *
 * @param  {EventTarget}     target  target node
 * @param  {array, string}   events   events to add
 * @param  {function}        handler  event handler
 * @param  {object, boolean} options  event options
 */
export const addEventListeners = (target, events, handler, options) => {
  if (!Array.isArray(events)) {
    events = events.split(" ");
  }

  events.forEach((event) => {
    target.addEventListener(event, handler, options);
  });
};

/**
 * removeEventListeners
 *
 * Allow multiple events to be removed in one call, jQuery-style
 *
 * @param  {EventTarget}     target  target node
 * @param  {array, string}   events   events to remove
 * @param  {function}        handler  event handler
 * @param  {object, boolean} options  event options
 */
export const removeEventListeners = (target, events, handler, options) => {
  if (!Array.isArray(events)) {
    events = events.split(" ");
  }

  events.forEach((event) => {
    target.removeEventListener(event, handler, options);
  });
};

export const waitForEvent = (target, event) => {
  return new Promise((resolve) => {
    target.addEventListener(event, resolve, { once: true });
  });
};

export const waitForEvents = (target, events) => {
  if (!Array.isArray(events)) {
    events = events.split(" ");
  }

  return Promise.all(events.map((event) => waitForEvent(target, event)));
};

export const getAbsoluteOffset = (element) => {
  let top = 0;
  let left = 0;

  while (element) {
    top += element.offsetTop || 0;
    left += element.offsetLeft || 0;
    element = element.offsetParent;
  }

  return { left, top };
};

export const getEventPath = (event) => {
  const path = [];
  let currentElem = event.target;

  while (currentElem) {
    path.push(currentElem);
    currentElem = currentElem.parentElement;
  }

  if (!path.includes(window) && !path.includes(document)) path.push(document);
  if (!path.includes(window)) path.push(window);

  return path;
};

/**
 * TODO: TEST w/ fromPairs lodash replacement
 * @returns
 */
export const getViewportInfo = () => {
  const viewport = document.querySelector("meta[name=viewport]");
  if (!viewport || !viewport["content"])
    return { width: window.innerWidth, height: window.innerHeight };

  return Object.fromEntries(
    viewport["content"].split(",").map((content) =>
      content
        .trim()
        .split("=")
        .map((data, index) => {
          if (index === 1 && data.match(/\d+/)) return parseFloat(data);
          return data;
        })
    )
  );
};

export const splitTextIntoLetters = (text) => {
  const array = text.split("");
  return array.map((letter, i) => <span key={`${i}-${letter}`}>{letter}</span>);
};

export const getCSSRules = (element) => {
  const sheets = document.styleSheets;
  const matchingRules = [];
  element.matches = element.matches || element.webkitMatchesSelector;

  Array.from(sheets).forEach((sheet) => {
    const rules = sheet.rules || sheet.cssRules;

    Array.from(rules).forEach((rule) => {
      if (element.matches(rule.selectorText)) matchingRules.push(rule.style);
    });
  });

  return matchingRules;
};

export const remToPx = (rem) =>
  isBrowser()
    ? rem * parseFloat(getComputedStyle(document.documentElement).fontSize)
    : 0;
